import { Component, ElementRef, HostListener, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
  AllowedFileExtensions,
  ChatFeedback,
  ChatFile,
  ChatMessage,
  ChatResponse,
  ChatSender,
  ConversationDetail,
  ConversationMessage,
  Emojis,
  MaxFileSizeBytes,
  MaxFilesPerConversation,
  SessionSummary,
  SuggestedQuestion,
  SuggestedQuestions,
  User,
} from '../_models';
import { AlertService, AskSolvpathService, UserService } from '../_services';

@Component({
  moduleId: module.id.toString(),
  selector: 'app-ask-solvpath',
  templateUrl: './ask-solvpath.component.html',
  styleUrls: ['./ask-solvpath.component.scss'],
})
export class AskSolvpathComponent implements OnInit {
  currentUser: User;

  isOpen: boolean = false;
  chatStarted: boolean = false;
  isEmojiMenuOpen: boolean = false;
  isChatbotAvailable: boolean = true;
  message: string = '';
  chatTitle: string = '';
  conversationId: string = null;
  messages: ChatMessage[] = [];
  isBotTyping: boolean = false;
  showFeedback: boolean = false;
  chatFeedback: ChatFeedback = null;
  ChatFeedback = ChatFeedback;
  ChatSender = ChatSender;
  attachedFiles: ChatFile[] = [];
  isUploadingFile: boolean = false;
  isHistoryOpen: boolean = false;
  isLoadingHistory: boolean = false;
  recentChats: SessionSummary[] = [];
  recentChatsLimit: number = 6;
  panelWidth: number = 420;
  panelHeight: number = 600;

  private resizeStartX: number;
  private resizeStartY: number;
  private resizeStartWidth: number;
  private resizeStartHeight: number;
  private resizeMaxWidth: number;
  private resizeMoveListener = (event: MouseEvent) => this.onResizeMove(event);
  private resizeEndListener = () => this.stopResize();

  emojis: string[] = Emojis;

  suggestedQuestions: SuggestedQuestion[] = SuggestedQuestions;

  @ViewChild('messageInput', { static: false }) messageInput: ElementRef;
  @ViewChild('chatThreadEnd', { static: false }) chatThreadEnd: ElementRef;
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;
  @ViewChildren('messageGroup') messageGroups: QueryList<ElementRef>;

  constructor(
    private askSolvpathService: AskSolvpathService,
    private alertService: AlertService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.fetchCurrentUser();
    this.checkAvailability();
  }

  fetchCurrentUser() {
    this.userService.getCurrent().subscribe((user: User) => {
      this.currentUser = user;
    });
  }

  checkAvailability() {
    this.askSolvpathService.checkHealth().subscribe(
      () => {
        this.isChatbotAvailable = true;
      },
      () => {
        this.isChatbotAvailable = false;
      }
    );
  }

  toggle(event: Event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  close() {
    this.isOpen = false;
    this.isEmojiMenuOpen = false;
    this.isHistoryOpen = false;
  }

  @HostListener('document:click')
  closeOnOutsideClick() {
    if (this.isOpen) {
      this.isOpen = false;
      this.isEmojiMenuOpen = false;
      this.isHistoryOpen = false;
    }
  }

  closeEmojiMenu() {
    this.isEmojiMenuOpen = false;
  }

  closeHistoryMenu() {
    this.isHistoryOpen = false;
  }

  toggleHistory(event: Event) {
    event.stopPropagation();
    this.isHistoryOpen = !this.isHistoryOpen;

    if (this.isHistoryOpen) {
      this.recentChatsLimit = 5;
      this.loadRecentChats();
    }
  }

  loadRecentChats() {
    this.isLoadingHistory = true;

    this.askSolvpathService.listConversations(this.recentChatsLimit).subscribe(
      (response: { items: SessionSummary[] }) => {
        this.isLoadingHistory = false;
        this.recentChats = response.items;
      },
      () => {
        this.isLoadingHistory = false;
        this.alertService.error('Could not load recent chats. Please try again.');
      }
    );
  }

  loadMoreRecentChats() {
    this.recentChatsLimit += 10;
    this.loadRecentChats();
  }

  openChat(session: SessionSummary) {
    this.isLoadingHistory = true;

    this.askSolvpathService.getConversation(session.id).subscribe(
      (conversation: ConversationDetail) => {
        this.isLoadingHistory = false;
        this.conversationId = conversation.id;
        this.chatTitle = conversation.title;
        this.chatStarted = true;
        this.isHistoryOpen = false;
        this.showFeedback = false;
        this.chatFeedback = null;
        this.attachedFiles = [];

        this.messages = (conversation.messages || []).map((message: ConversationMessage) => ({
          sender: message.role,
          text: message.content,
          timestamp: new Date(message.created_at),
          turnId: message.role === ChatSender.Assistant ? message.turn_id : null,
        }));

        this.scrollThreadToBottom();
      },
      () => {
        this.isLoadingHistory = false;
        this.alertService.error('Could not open that chat. Please try again.');
      }
    );
  }

  deleteChat(session: SessionSummary, event: Event) {
    event.stopPropagation();

    const confirmed = window.confirm('Are you sure you want to delete this chat? This cannot be undone.');

    if (confirmed) {
      this.askSolvpathService.deleteConversation(session.id).subscribe(
        () => {
          this.loadRecentChats();

          if (this.conversationId === session.id) {
            this.startNewChat();
          }
        },
        () => {
          this.alertService.error('Could not delete that chat. Please try again.');
        }
      );
    }
  }

  toggleEmojiMenu(event: Event) {
    event.stopPropagation();
    this.isEmojiMenuOpen = !this.isEmojiMenuOpen;
  }

  selectEmoji(emoji: string) {
    this.message = this.message + emoji;

    if (this.messageInput && this.messageInput.nativeElement) {
      this.messageInput.nativeElement.focus();
    }
  }

  startChat() {
    this.chatStarted = true;

    if (this.messageInput && this.messageInput.nativeElement) {
      this.messageInput.nativeElement.focus();
    }
  }

  startNewChat() {
    if (this.conversationId) {
      this.askSolvpathService.endConversation(this.conversationId).subscribe(() => {}, () => {});
    }

    this.messages = [];
    this.isBotTyping = false;
    this.showFeedback = false;
    this.chatFeedback = null;
    this.chatTitle = '';
    this.conversationId = null;
    this.message = '';
    this.attachedFiles = [];
    this.isHistoryOpen = false;

    if (this.messageInput && this.messageInput.nativeElement) {
      this.messageInput.nativeElement.focus();
    }
  }

  sendMessage(question: string) {
    let trimmedQuestion = question ? question.trim() : '';

    if (trimmedQuestion && !this.isBotTyping) {
      const isNewConversation = !this.conversationId;
      this.chatStarted = true;
      this.messages.push({ sender: ChatSender.User, text: trimmedQuestion, timestamp: new Date() });
      this.isBotTyping = true;
      this.showFeedback = false;
      this.chatFeedback = null;
      this.scrollThreadToBottom();

      this.askSolvpathService.sendMessage(trimmedQuestion, this.conversationId).subscribe(
        (response: ChatResponse) => {
          this.isBotTyping = false;
          this.conversationId = response.conversation_id;
          this.messages.push({
            sender: ChatSender.Assistant,
            text: response.answer,
            timestamp: new Date(),
            turnId: response.trace ? response.trace.turn_id : null,
          });
          this.scrollToLatestMessage();

          if (isNewConversation) {
            this.refreshChatTitle();
          }

          if (this.getAssistantMessageCount() >= 3) {
            setTimeout(() => {
              this.showFeedback = true;
            }, 800);
          }
        },
        () => {
          this.isBotTyping = false;
          this.messages.push({
            sender: ChatSender.Assistant,
            text: 'Something went wrong answering that. Please try again in a moment.',
            timestamp: new Date(),
          });
          this.scrollToLatestMessage();
        }
      );
    }
  }

  openFilePicker() {
    if (this.attachedFiles.length >= MaxFilesPerConversation) {
      this.alertService.error(
        `You can attach up to ${MaxFilesPerConversation} files per conversation. Detach one first.`
      );
    } else if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    input.value = '';

    if (file) {
      const nameParts = file.name.split('.');
      const extension = '.' + nameParts[nameParts.length - 1].toLowerCase();

      if (AllowedFileExtensions.indexOf(extension) === -1) {
        this.alertService.error(`That file type isn't supported. Allowed types: ${AllowedFileExtensions.join(', ')}`);
      } else if (file.size > MaxFileSizeBytes) {
        this.alertService.error('That file is too large. Max size is 50 MB.');
      } else if (this.conversationId) {
        this.uploadFile(file);
      } else {
        this.isUploadingFile = true;

        this.askSolvpathService.createConversation().subscribe(
          (session: SessionSummary) => {
            this.conversationId = session.id;
            this.uploadFile(file);
          },
          () => {
            this.isUploadingFile = false;
            this.alertService.error('Could not start a conversation for this file. Please try again.');
          }
        );
      }
    }
  }

  uploadFile(file: File) {
    this.isUploadingFile = true;

    this.askSolvpathService.uploadFile(this.conversationId, file).subscribe(
      (chatFile: ChatFile) => {
        this.isUploadingFile = false;
        this.chatStarted = true;
        this.attachedFiles.push(chatFile);
      },
      (error) => {
        this.isUploadingFile = false;
        let detail = 'Could not upload that file. Please try again.';

        if (error && error.error && error.error.detail) {
          detail = error.error.detail;
        }

        this.alertService.error(detail);
      }
    );
  }

  removeAttachedFile(file: ChatFile) {
    this.askSolvpathService.detachFile(this.conversationId, file.id).subscribe(
      () => {
        this.attachedFiles = this.attachedFiles.filter((attachedFile: ChatFile) => attachedFile.id !== file.id);
      },
      () => {
        this.alertService.error('Could not remove that file. Please try again.');
      }
    );
  }

  refreshChatTitle() {
    const conversationId = this.conversationId;

    this.askSolvpathService.getConversation(conversationId).subscribe(
      (conversation: ConversationDetail) => {
        if (conversation && conversation.title) {
          this.chatTitle = conversation.title;
        }
      },
      () => {}
    );

    // The initial title is auto-derived synchronously, but a nicer one is generated by a
    // background LLM call a moment later - re-fetch once to pick that up if it's still current.
    setTimeout(() => {
      if (this.conversationId === conversationId) {
        this.askSolvpathService.getConversation(conversationId).subscribe(
          (conversation: ConversationDetail) => {
            if (conversation && conversation.title) {
              this.chatTitle = conversation.title;
            }
          },
          () => {}
        );
      }
    }, 4000);
  }

  getRelativeTime(timestamp: Date): string {
    const elapsedSeconds = Math.floor((new Date().getTime() - timestamp.getTime()) / 1000);
    let label = 'Just now';

    if (elapsedSeconds >= 3600) {
      label = Math.floor(elapsedSeconds / 3600) + 'h ago';
    } else if (elapsedSeconds >= 60) {
      label = Math.floor(elapsedSeconds / 60) + 'm ago';
    }

    return label;
  }

  getAssistantMessages(): ChatMessage[] {
    return this.messages.filter((chatMessage: ChatMessage) => chatMessage.sender === ChatSender.Assistant);
  }

  getAssistantMessageCount(): number {
    return this.getAssistantMessages().length;
  }

  setFeedback(feedback: ChatFeedback) {
    this.chatFeedback = feedback;

    const assistantMessages = this.getAssistantMessages();
    const lastAssistantMessage = assistantMessages.length ? assistantMessages[assistantMessages.length - 1] : null;

    if (lastAssistantMessage && lastAssistantMessage.turnId) {
      this.askSolvpathService.submitFeedback(lastAssistantMessage.turnId, feedback).subscribe(() => {}, () => {});
    }

    setTimeout(() => {
      this.showFeedback = false;
    }, 2500);
  }

  scrollThreadToBottom() {
    setTimeout(() => {
      if (this.chatThreadEnd && this.chatThreadEnd.nativeElement) {
        this.chatThreadEnd.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    });
  }

  scrollToLatestMessage() {
    setTimeout(() => {
      if (this.messageGroups && this.messageGroups.last) {
        this.messageGroups.last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  startResize(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.panelWidth;
    this.resizeStartHeight = this.panelHeight;

    // Don't let the panel grow wide enough to overlap the left nav sidebar.
    const edgeMargin = 12;
    const panelRightOffset = 20;
    const sidebarEl = document.querySelector('.default-sidebar');
    const sidebarRight = sidebarEl ? sidebarEl.getBoundingClientRect().right : 0;
    this.resizeMaxWidth = Math.min(window.innerWidth - 90, window.innerWidth - panelRightOffset - sidebarRight - edgeMargin);

    document.addEventListener('mousemove', this.resizeMoveListener);
    document.addEventListener('mouseup', this.resizeEndListener);
  }

  onResizeMove(event: MouseEvent) {
    // The panel is anchored via `top: 65px;` in the stylesheet - this must match so the
    // panel's bottom edge never gets pushed past the viewport while resizing.
    const panelTopOffset = 65;
    const edgeMargin = 12;
    const minWidth = 320;
    const minHeight = 420;
    const maxHeight = window.innerHeight - panelTopOffset - edgeMargin;
    const nextWidth = this.resizeStartWidth + (this.resizeStartX - event.clientX);
    const nextHeight = this.resizeStartHeight + (event.clientY - this.resizeStartY);

    this.panelWidth = Math.min(this.resizeMaxWidth, Math.max(minWidth, nextWidth));
    this.panelHeight = Math.min(maxHeight, Math.max(minHeight, nextHeight));
  }

  stopResize() {
    document.removeEventListener('mousemove', this.resizeMoveListener);
    document.removeEventListener('mouseup', this.resizeEndListener);
  }
}
